import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyCMYM1Y-kvMTFp1EogB0lB4im-EKleSwv8",
  authDomain: "scope-check-456316.firebaseapp.com",
  projectId: "scope-check-456316",
  storageBucket: "scope-check-456316.firebasestorage.app",
  messagingSenderId: "266654428421",
  appId: "1:266654428421:web:8ff34dd20f012c938cdb6c"
};

const app = initializeApp(firebaseConfig);

/** localStorage key for the FCM device token (used by app and logout). */
export const FCM_TOKEN_STORAGE_KEY = "fcm_device_token";
/** localStorage key for when the token was stored (used to expire after 11 hours, same as auth). */
export const FCM_TOKEN_SAVED_AT_KEY = "fcm_device_token_saved_at";

/** Token treated as expired after 11 hours (same as auth token cookie). */
const FCM_TOKEN_EXPIRY_MS = 11 * 60 * 60 * 1000;

const VAPID_KEY = "BAaLPsmxURqFwAXWWo5ei6QDRlP_xRSaSckRs58DUMf_b9Vchny4d35Ey0auwP_p9xJVVxBmIjmK8ed2ZFwb9lk";
let messagingInstancePromise = null;

const isSecureMessagingContext = () => {
  if (typeof window === "undefined") return false;

  if (window.isSecureContext) return true;

  const hostname = window.location?.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
};

export const isFirebaseMessagingAvailable = async () => {
  if (typeof window === "undefined") return false;
  if (!isSecureMessagingContext()) return false;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return false;
  if (!("Notification" in window)) return false;

  try {
    return await isSupported();
  } catch (_) {
    return false;
  }
};

const getMessagingInstance = async () => {
  if (!messagingInstancePromise) {
    messagingInstancePromise = isFirebaseMessagingAvailable()
      .then((supported) => (supported ? getMessaging(app) : null))
      .catch(() => null);
  }

  return messagingInstancePromise;
};

/**
 * Clear stored FCM token and saved-at timestamp (e.g. when expired or on logout).
 */
const clearStoredFcmToken = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
      localStorage.removeItem(FCM_TOKEN_SAVED_AT_KEY);
    }
  } catch (_) { }
};

/**
 * Get the FCM token stored on this device. Returns null if missing or expired (after 11 hours).
 */
export const getStoredToken = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const savedAt = localStorage.getItem(FCM_TOKEN_SAVED_AT_KEY);
    if (savedAt) {
      const age = Date.now() - Number(savedAt);
      if (age > FCM_TOKEN_EXPIRY_MS) {
        clearStoredFcmToken();
        return null;
      }
    }
    return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Request and store FCM token for this device. Call once when app loads (after permission).
 * Uses the same service worker that receives background/closed-app pushes so notifications
 * work when the app is not in recent or in background.
 */
export const requestForToken = async () => {
  try {
    const messaging = await getMessagingInstance();
    if (!messaging) return null;

    // Bind token to our firebase-messaging-sw.js so FCM delivers to it when app is closed/background
    let serviceWorkerRegistration = null;
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      try {
        await navigator.serviceWorker.ready;
        serviceWorkerRegistration = await navigator.serviceWorker.getRegistration();
      } catch (_) { }
    }

    const options = { vapidKey: VAPID_KEY };
    if (serviceWorkerRegistration) {
      options.serviceWorkerRegistration = serviceWorkerRegistration;
    }
    const currentToken = await getToken(messaging, options);

    if (currentToken) {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          localStorage.setItem(FCM_TOKEN_STORAGE_KEY, currentToken);
          localStorage.setItem(FCM_TOKEN_SAVED_AT_KEY, String(Date.now()));
        }
      } catch (e) {
        // localStorage may be full or disabled
      }
      // console.log("✅ FCM Token (this device):", currentToken);
      return currentToken;
    } else {
      console.log("❌ No registration token available.");
      return null;
    }
  } catch (err) {
    console.error("❌ Error getting token:", err);
    return null;
  }
};

/**
 * Ensure this device has an FCM token: request permission, then get and store token.
 * Safe to call on every app load; no backend calls.
 */
export const ensureTokenForThisDevice = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) return null;
  if (!(await isFirebaseMessagingAvailable())) return null;
  if (Notification.permission === "granted") {
    return requestForToken();
  }
  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission === "granted") return requestForToken();
  }
  return null;
};

export const onMessageListener = () =>
  new Promise(async (resolve, reject) => {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      reject(new Error("Firebase messaging unavailable in this browser context."));
      return;
    }

    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
